import type { MonthlyUserBreakdown, PaySchedule, RouteAssignment, UserId } from '@/types';
import { DEFAULT_PAY_SCHEDULE, getReportPeriod } from '@/utils/date';
import { computeRouteDurationHours } from '@/utils/routeMetrics';

/** Friendly placeholder shown whenever a uid can't be resolved to a name. */
export const UNKNOWN_USER_LABEL = 'Unknown driver';

/**
 * Groups a month's assignment episodes by user, counting only episodes that
 * were actually run (never unassigned) toward totals — mirrors the same
 * "completed" definition used in the personal history logbook.
 */
export const buildMonthlyBreakdown = (
  assignments: RouteAssignment[],
  nameByUserId: Record<UserId, string>,
  payScheduleByUserId: Record<UserId, PaySchedule>,
  endingMonth: string,
): MonthlyUserBreakdown[] => {
  const totals = new Map<UserId, MonthlyUserBreakdown>();

  for (const assignment of assignments) {
    if (assignment.status !== 'assigned') continue;
    const paySchedule = payScheduleByUserId[assignment.userId] ?? DEFAULT_PAY_SCHEDULE;
    const { startDate, endDate, endDateExclusive } = getReportPeriod(paySchedule, endingMonth);
    if (assignment.date < startDate || assignment.date >= endDateExclusive) continue;

    const isExtra = assignment.kind === 'extra';

    const existing = totals.get(assignment.userId) ?? {
      userId: assignment.userId,
      displayName: nameByUserId[assignment.userId] ?? UNKNOWN_USER_LABEL,
      paySchedule,
      periodStart: startDate,
      periodEnd: endDate,
      routeCount: 0,
      totalHours: 0,
      totalEarnings: 0,
    };

    if (isExtra) {
      existing.totalHours +=
        assignment.extraDurationHours ?? assignment.routeSnapshot.extraDurationHours ?? 0;
    } else {
      existing.routeCount += 1;
      existing.totalHours += computeRouteDurationHours(
        assignment.routeSnapshot.startTime,
        assignment.routeSnapshot.endTime,
      );
      existing.totalEarnings += assignment.routeSnapshot.payAmount;
    }

    totals.set(assignment.userId, existing);
  }

  return [...totals.values()].sort((a, b) => b.totalEarnings - a.totalEarnings);
};
